import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Shield,
  Heart,
  GraduationCap,
  ArrowRight,
  CheckCircle,
  Target,
  Users,
  FileText,
  Clock,
  Award,
  MessageCircle,
  ChevronDown,
  Calendar,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import IntakeModal from "../components/IntakeModal";
import { DIVISIONS, COMPANY } from "../lib/divisions";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const HomePage = () => {
  const [showIntake, setShowIntake] = useState(false);

  return (
    <div className="pt-20 lg:pt-24">
      <Hero />
      <Divisions />
      <WhyUs />
      <Process />
      <Testimonials />
      <FAQ />
      <FinalCTA onIntake={() => setShowIntake(true)} />
      <IntakeModal isOpen={showIntake} onClose={() => setShowIntake(false)} />
    </div>
  );
};

const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden">
    <div className="absolute inset-0 z-0">
      <img
        src="https://images.unsplash.com/photo-1676286155307-9fd53cd4b431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhbWVyaWNhbiUyMGZsYWclMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3Njk4Njg1NDZ8MA&ixlib=rb-4.1.0&q=85"
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
    </div>

    <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#C89B3C]/10 rounded-full blur-3xl z-0" />

    <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 lg:py-28 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
          <span className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500 mb-3 block">
            Noble Strategies Consulting
          </span>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-[#0B2343] leading-[1.05] mb-6">
            Strategy.<br />
            Evidence.<br />
            <span className="text-[#C89B3C]">Results.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
            Helping veterans, families, and individuals navigate disability consulting,
            insurance solutions, and educational opportunities with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/schedule"
              data-testid="hero-schedule-cta"
              className="bg-[#0B2343] text-white px-8 py-4 font-medium tracking-wide hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <Calendar size={20} />
              Schedule Consultation
            </Link>
            <a
              href="#divisions"
              data-testid="hero-explore-cta"
              className="border-2 border-[#0B2343] text-[#0B2343] hover:bg-[#0B2343] hover:text-white px-8 py-4 font-medium tracking-wide transition-all inline-flex items-center justify-center gap-2"
            >
              Explore Services
              <ArrowRight size={18} />
            </a>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200 grid grid-cols-3 gap-6 max-w-lg">
            <TrustBadge icon={<Shield size={20} />} label="Veteran Owned" />
            <TrustBadge icon={<CheckCircle size={20} />} label="No Upfront Fees" />
            <TrustBadge icon={<Award size={20} />} label="Nationwide" />
          </div>
        </motion.div>

        {/* Right visual */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="relative hidden lg:block"
        >
          <div className="relative aspect-[4/5] overflow-hidden">
            <div className="absolute inset-0 bg-slate-900/5" />
            <img
              src="/soldier-transition.png"
              alt="Soldier transitioning to civilian life"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 right-0 w-28 h-28 bg-[#C89B3C]" />
            <div className="absolute bottom-6 right-6 text-white font-serif text-sm text-right">
              <span className="block font-bold text-3xl">100%</span>
              <span>Dedication</span>
            </div>
          </div>
          <div className="absolute -left-6 bottom-24 bg-white p-6 shadow-2xl border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#0B2343] flex items-center justify-center">
                <FileText className="text-[#C89B3C]" size={22} />
              </div>
              <div>
                <span className="block text-slate-900 font-bold">Free Case Review</span>
                <span className="text-slate-500 text-sm">No obligation consultation</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const TrustBadge = ({ icon, label }) => (
  <div className="flex items-center gap-2">
    <span className="text-[#C89B3C]">{icon}</span>
    <span className="text-sm text-slate-700 font-medium">{label}</span>
  </div>
);

const divisionCards = [
  {
    ...DIVISIONS.veteran,
    icon: Shield,
    bullets: [
      "Evidence development & records review",
      "C&P appointment preparation",
      "Decision letter reviews & appeals",
    ],
  },
  {
    ...DIVISIONS.insurance,
    icon: Heart,
    bullets: [
      "Life & final expense coverage",
      "Mortgage & income protection",
      "Retirement-focused solutions",
    ],
  },
  {
    ...DIVISIONS.education,
    icon: GraduationCap,
    bullets: [
      "VR&E walkthroughs",
      "GI Bill & scholarship guidance",
      "College, trade & career planning",
    ],
  },
];

const Divisions = () => (
  <section
    id="divisions"
    data-testid="divisions-section"
    className="py-24 md:py-28 bg-slate-50"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <SectionHeading
        eyebrow="Our Divisions"
        title="Three focused practices. One trusted firm."
        subtitle="From VA disability strategy to insurance planning and education support, we help clients move forward with clarity."
        align="center"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {divisionCards.map((d, idx) => {
          const Icon = d.icon;
          return (
            <motion.div
              key={d.id}
              {...fadeInUp}
              transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
              className="group bg-white p-8 border border-slate-200 hover:border-[#C89B3C] hover:shadow-xl transition-all duration-300 flex flex-col"
              data-testid={`division-card-${d.id}`}
            >
              <div className="w-14 h-14 bg-[#0B2343] flex items-center justify-center mb-6 group-hover:bg-[#C89B3C] transition-colors">
                <Icon className="text-[#C89B3C] group-hover:text-white transition-colors" size={26} />
              </div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-3">{d.name}</h3>
              <p className="text-slate-600 mb-5">{d.tagline}</p>
              <ul className="space-y-2 mb-8 flex-1">
                {d.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle size={16} className="text-[#C89B3C] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={d.slug}
                className="inline-flex items-center gap-2 text-[#0B2343] font-medium hover:text-[#C89B3C] transition-colors border-t border-slate-200 pt-5"
                data-testid={`division-cta-${d.id}`}
              >
                Learn more <ArrowRight size={16} />
              </Link>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const WhyUs = () => {
  const items = [
    { icon: Target, title: "Mission Focused", desc: "Clear objectives and measurable outcomes for every client engagement." },
    { icon: Users, title: "Veteran First", desc: "Founded and led by veterans who understand the challenges you face." },
    { icon: FileText, title: "Evidence Driven", desc: "Every recommendation is backed by records, documentation, and strategy." },
    { icon: Clock, title: "No Upfront Fees", desc: "Consultations are free. You always know what to expect before you commit." },
  ];

  return (
    <section className="py-24 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <SectionHeading
          eyebrow="Why Noble Strategies"
          title="Personal guidance you can trust."
          subtitle="We combine strategy, evidence, and empathy so that every client — veteran, family member, or individual — feels supported at every step."
          align="center"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                {...fadeInUp}
                transition={{ ...fadeInUp.transition, delay: idx * 0.08 }}
                className="p-6 bg-slate-50 border-l-4 border-[#C89B3C]"
              >
                <Icon className="text-[#C89B3C] mb-4" size={28} />
                <h3 className="font-serif text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Process = () => {
  const steps = [
    { n: "01", title: "Free Consultation", desc: "We listen first — understanding your goals and situation with no obligation or pressure." },
    { n: "02", title: "Strategic Review", desc: "We evaluate the strongest path forward, whether that's a claim, a policy, or a program." },
    { n: "03", title: "Evidence & Planning", desc: "We organize documentation, coordinate professionals, and build a clear plan of action." },
    { n: "04", title: "Guided Execution", desc: "We walk with you through submission, follow-up, and every milestone to your goal." },
  ];

  return (
    <section id="process" className="py-24 md:py-28 bg-[#0B2343] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]">
        <img
          src="https://images.unsplash.com/photo-1759267643458-d5168cad1157?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhbWVyaWNhbiUyMGZsYWclMjBkZXRhaWx8ZW58MHx8fHwxNzY5ODYzODE4fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <SectionHeading
          eyebrow="Our Process"
          title="A clear, structured approach."
          subtitle="We remove the guesswork and replace it with a proven, four-step framework."
          align="center"
          light
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, idx) => (
            <motion.div
              key={step.n}
              {...fadeInUp}
              transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
              className="bg-white/5 border border-white/10 p-6 backdrop-blur-sm hover:border-[#C89B3C]/60 transition-colors"
            >
              <span className="font-mono text-[#C89B3C] text-sm tracking-wider">{step.n}</span>
              <h3 className="font-serif text-xl font-bold text-white mt-2 mb-3">{step.title}</h3>
              <p className="text-slate-300 text-sm leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const testimonials = [
  {
    quote:
      "Noble Strategies helped me organize my records and prepare for my C&P exam. Their guidance made all the difference.",
    name: "Anthony R.",
    role: "U.S. Army Veteran",
  },
  {
    quote:
      "Straightforward, patient, and genuinely helpful. They took the time to explain everything without pressure.",
    name: "Maria S.",
    role: "Military Spouse",
  },
  {
    quote:
      "The strategy they built around my education benefits was clear and easy to follow. Highly recommend.",
    name: "David P.",
    role: "USMC Veteran",
  },
];

const Testimonials = () => (
  <section className="py-24 md:py-28 bg-slate-50">
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <SectionHeading
        eyebrow="Client Voices"
        title="Trusted by those who serve — and their families."
        align="center"
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {testimonials.map((t, idx) => (
          <motion.div
            key={t.name}
            {...fadeInUp}
            transition={{ ...fadeInUp.transition, delay: idx * 0.1 }}
            className="bg-white p-8 border border-slate-200"
          >
            <MessageCircle className="text-[#C89B3C] mb-4" size={28} />
            <p className="text-slate-700 leading-relaxed mb-6 italic">"{t.quote}"</p>
            <div className="border-t border-slate-100 pt-4">
              <p className="font-semibold text-slate-900">{t.name}</p>
              <p className="text-sm text-slate-500">{t.role}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const faqs = [
  {
    q: "Is Noble Strategies affiliated with the VA?",
    a: "No. We are an independent, veteran-owned consulting firm. We are not affiliated with, endorsed by, or connected to the U.S. Department of Veterans Affairs.",
  },
  {
    q: "Do you provide legal advice?",
    a: "No. We provide consulting and advocacy services only. We are not a law firm and do not provide legal representation.",
  },
  {
    q: "Do you charge for the initial consultation?",
    a: "No. All initial consultations are free with no obligation.",
  },
  {
    q: "Can you guarantee a specific VA rating or outcome?",
    a: "No consultant, attorney, or advocate can guarantee outcomes. VA decisions depend on evidence, medical records, and adjudication.",
  },
  {
    q: "Do you work with civilians and families as well?",
    a: "Yes. Our Insurance Solutions and Education Assistance divisions serve veterans, military families, and civilian clients alike.",
  },
];

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="py-24 md:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-8">
        <SectionHeading
          eyebrow="FAQ"
          title="Frequently asked questions."
          subtitle="If you don't see your question here, reach out — we're happy to help."
          align="center"
        />
        <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
          {faqs.map((f, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div key={f.q}>
                <button
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  className="w-full flex items-center justify-between py-5 text-left"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="font-serif text-lg font-semibold text-slate-900 pr-4">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={22}
                    className={`text-[#C89B3C] flex-shrink-0 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="pb-5 text-slate-600 leading-relaxed">{f.a}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FinalCTA = ({ onIntake }) => (
  <section className="py-24 md:py-28 bg-[#0B2343] text-white relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.05]">
      <img
        src="https://images.unsplash.com/photo-1676286155307-9fd53cd4b431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhbWVyaWNhbiUyMGZsYWclMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3Njk4Njg1NDZ8MA&ixlib=rb-4.1.0&q=85"
        alt=""
        className="w-full h-full object-cover"
      />
    </div>
    <div className="max-w-4xl mx-auto px-6 md:px-8 text-center relative z-10">
      <div className="h-[3px] w-14 bg-[#C89B3C] mx-auto mb-6" />
      <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to move forward?
      </h2>
      <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
        Whether you need help with a VA claim, an insurance decision, or an education plan —
        book a free consultation and let's map out the next step together.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/schedule"
          data-testid="final-cta-schedule"
          className="bg-[#C89B3C] text-white px-8 py-4 font-medium tracking-wide hover:bg-[#a8852f] transition-all inline-flex items-center justify-center gap-2"
        >
          <Calendar size={20} />
          Schedule Consultation
        </Link>
        <button
          onClick={onIntake}
          data-testid="final-cta-intake"
          className="border-2 border-white text-white hover:bg-white hover:text-[#0B2343] px-8 py-4 font-medium tracking-wide transition-all inline-flex items-center justify-center gap-2"
        >
          Veteran Intake Form
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  </section>
);

export default HomePage;
